import { type LocationQuery } from 'vue-router';
import HotelPackage from '@itinerary/hotel/HotelPackage';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import { type Location, type ReserveInfo } from '@utils/types';
import type ItineraryPackageSet from '@itinerary/ItineraryPackageSet';
import type PriceClass from '@itinerary/PriceClass';
import BusPackage from '@itinerary/trip/bus/BusPackage';
import FlightPackage from '@itinerary/trip/flight/FlightPackage';
import TrainPackage from '@itinerary/trip/train/TrainPackage';
import {
    ServerDisconnectToDateException,
    TrainNoServerResponseError,
} from '@itinerary/Exceptions';
import {
    type EmptyListReason,
    type Filter,
    type Filters,
    type ListErrorMV,
    LoadingStatus,
    NetworkError,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import {
    AutoReserveType,
    type ItineraryParams,
    ReserveType,
} from '@utils/itinerary';
import {
    type ItineraryPackageLoaderConfig,
    type TrackingType,
} from '@itinerary/types';
import type { AnalyticsAttribute } from '@store/analytics';
import type TripModule from '@itinerary/trip/TripModule';
import { RoutePackage } from '@itinerary/trip/multiRoute/RoutePackage';

export default abstract class ItineraryModule<
    TPrice extends PriceClass = PriceClass,
    TFilters extends Filters = Filters,
    TLocation extends Location<TLocation> = Location,
    TPackage extends ItineraryPackage<TPrice> = ItineraryPackage<TPrice>,
    TPackageSet extends ItineraryPackageSet<
        TFilters['meta'],
        TFilters['key'],
        TPackage
    > = ItineraryPackageSet<TFilters['meta'], TFilters['key'], TPackage>,
> {
    //#region state

    get isSingleForRoundTrip() {
        return true;
    }

    get routeNotFound() {
        return this.currentPackageSet?.metaData?.routeNotFound ?? false;
    }

    get searchResultAttributes(): AnalyticsAttribute {
        return {
            hasMultiLeg: this.currentItineraryPackages?.some(
                pkg => pkg.hasMultiLeg
            ),
            totalReservableCards: this.sortedReservableList.length,
            totalNonReservableCards: this.sortedNonReservableList.length,
            totalCapacity: this.sortedReservableList.reduce(
                (acc, current) =>
                    acc + (current.selectedPrices?.[0]?.capacity ?? 0),
                0
            ),
        };
    }

    routeExtraInfoTemplate(_params: {
        departurePackage: ItineraryPackage;
        returnPackage?: ItineraryPackage;
    }): null | string {
        return null;
    }

    lateralPackage: BusPackage | null = null;

    abstract get metaTagTitle(): string;

    itineraryPackageSets: (TPackageSet | null)[] = [];

    abstract sortBy: string;

    isInitialSort = true;

    listErrorReason: ListErrorMV | null = null;
    //#endregion

    get defaultAutoReserveType(): AutoReserveType {
        return AutoReserveType.None;
    }

    get showChangeDateSuggestion(): boolean {
        return !this.routeNotFound;
    }

    protected abstract get reserveInfo(): ReserveInfo;

    get unknownListError(): ListErrorMV {
        return {
            id: 'connection-lost',
            type: 'Unknown',
            image: '/img/error/connectionLost.svg',
            message: 'متاسفیم!',
            description:
                'دریافت اطلاعات انجام نشد. اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.',
        };
    }

    /**
     * return an error text for empty list
     *
     * @readonly
     * @abstract
     * @type {EmptyListReason}
     * @memberof ItineraryModule
     */
    abstract get emptyListReason(): EmptyListReason;
    get isSelectingReturn(): boolean {
        return useItineraryStore().isSelectingReturn;
    }

    terminatePackageReserve(_reserveId: number): void | Promise<void> {
        // empty body for parent classes
        // may be overridden by child classes if needed.
    }

    /**
     * reset last load time of loaders
     *
     * @memberof ItineraryModule
     */
    resetLoaders() {
        this.itineraryPackageSets.forEach(ps => ps?.resetLastLoad());
    }

    /**
     * get sort by list of current itinerary module
     *
     * @readonly
     * @abstract
     * @type {SortBy[]}
     * @memberof ItineraryModule
     */
    abstract get sortByList(): SortBy[];

    /**
     * Sets the values of applied filters
     */
    setFilterValues(
        filters: { [key in TFilters['key']]: any } | null | undefined
    ) {
        const currentIndex = useItineraryStore().currentSetIndex;
        if (!this.itineraryPackageSets[currentIndex] || !filters) {
            return;
        }
        this.itineraryPackageSets[currentIndex].filters = { ...filters };
    }

    get filterValues(): { [key in Filters['key']]: any } | null | undefined {
        const currentIndex = useItineraryStore().currentSetIndex;
        return this.itineraryPackageSets[currentIndex]?.filters;
    }

    /**
     * When overridden, specifies the default search method for the reserve
     *
     * @readonly
     * @abstract
     * @type {string}
     * @memberof ItineraryModule
     */
    abstract get defaultSortBy(): string;

    abstract applySort(list: ItineraryPackage[]): ItineraryPackage[];

    /**
     * get sorted list base on sortBy
     *
     * @readonly
     * @abstract
     * @memberof ItineraryModule
     */
    abstract get sortedReservableList(): ItineraryPackage[];

    abstract get sortedNonReservableList(): ItineraryPackage[];

    get hasAutoReserve(): boolean {
        return this.defaultAutoReserveType !== AutoReserveType.None;
    }

    get autoReserveOnly(): boolean {
        return this.hasAutoReserve && !this.sortedReservableList.length;
    }

    get autoReservePriceOnly(): boolean {
        if (this.hasAutoReserve) {
            return (
                this.autoReservableList.length === 0 &&
                !!this.sortedReservableList.length
            );
        }
        return false;
    }

    abstract get emptyPackage(): ItineraryPackage;

    get autoReservableList(): ItineraryPackage[] {
        return this.applySort(this.list.filter(pkg => pkg.autoReserveEnabled));
    }

    get autoReservePriceTrackingList(): ItineraryPackage[] {
        return this.applySort(
            this.list.filter(pkg => pkg.autoReservePriceTrackingEnabled)
        );
    }

    get extraAutoReserveItem(): ItineraryPackage | null {
        return null;
    }

    /**
     * GETTER - Returns the description for the view page
     *
     * @returns {string}
     * @memberof ItineraryModule
     */
    abstract get description(): string | null;

    /**
     * GETTER - When overridden, specified the description template that must be used in view page, for SEO's sake
     *
     * @returns {string}
     * @memberof ItineraryModule
     */
    abstract get descriptionTemplate(): string;

    /**
     * Clears the loaded information from availability service
     *
     * @memberof ItineraryModule
     */
    clear() {
        this.itineraryPackageSets.forEach(set => set?.stop());
        this.itineraryPackageSets = [];
    }

    /**
     * UTILITY - When overridden, returns an initial instance of filter object
     */
    abstract getInitialFilters(): { [key in TFilters['key']]: any };

    /**
     * get new package set base on loader
     *
     * @abstract
     * @param {ItineraryPackageLoader} loaderConfig
     * @returns {ItineraryPackageSet}
     * @memberof ItineraryModule
     */
    abstract getPackageSet(
        loaderConfig: ItineraryPackageLoaderConfig
    ): TPackageSet;

    abstract getItineraryPackageLoaderConfigs(
        params: ItineraryParams
    ): ItineraryPackageLoaderConfig[];

    needReinit(params: ItineraryParams) {
        const configs = this.getItineraryPackageLoaderConfigs(params);
        return configs.some((config, index) => {
            const packageSet = this.itineraryPackageSets[index];
            return !!packageSet?.needReinitialize(config);
        });
    }

    init(params: ItineraryParams) {
        const configs = this.getItineraryPackageLoaderConfigs(params);
        this.itineraryPackageSets = configs.map((config, index) => {
            const packageSet = this.itineraryPackageSets[index];
            if (packageSet) {
                packageSet.reinitialize(config, this.getInitialFilters());
                return packageSet;
            } else {
                return this.getPackageSet(config);
            }
        });
    }

    get expectedPackagesToBeSelected(): number {
        return 1;
    }

    /**
     * set last load time of an itinerary packageSet(base on index)
     *
     * @param {number} index
     * @param date
     * @memberof ItineraryModule
     */
    resetLastLoadTime({ index }: { index: number }) {
        const itineraryPackageSet = this.itineraryPackageSets[index];
        if (itineraryPackageSet) {
            itineraryPackageSet.resetLastLoad();
        }
    }

    /**
     * Call before select a package for do something base on module
     *
     * @abstract
     * @param {({
     *      package: ItineraryPackage | null;
     *      index?: number;
     * })} _params
     * @memberof ItineraryModule
     */
    beforeSelectPackage(_params: { package: ItineraryPackage; index: number }) {
        // empty body for parent class
        // may be overridden by child classes if needed.
    }

    /**
     * load available itinerary packages
     *
     * @param {{ index: number }} params
     * @returns {Promise<any>}
     * @memberof ItineraryModule
     */
    async loadAvailable(params: {
        index: number;
        inForm?: boolean;
    }): Promise<void> {
        const packageSet = this.itineraryPackageSets[params.index];
        if (!packageSet) {
            return;
        }
        // restore before load states
        this.listErrorReason = null;
        this.lateralPackage = null;

        const searchFormStore = useSearchFormStore();
        try {
            // Loading packages
            await packageSet.loadAvailable();
            this.sortBy = this.defaultSortBy;
            this.isInitialSort = true;
            this.lateralPackage = null;
            if (
                packageSet.list.every(item => !item.reservable) &&
                !params.inForm
            ) {
                await this.loadLaterals();
            }
        } catch (ex) {
            reportException(ex);
            if (ex instanceof NetworkError) {
                this.listErrorReason = {
                    id: 'network-error',
                    type: 'Network',
                    image: '',
                    message: 'network error',
                    description: 'unknown network error',
                };
            }
            if (ex instanceof ServerDisconnectToDateException) {
                this.listErrorReason = {
                    id: 'error-timeout',
                    type: 'Timeout',
                    image: this.unknownListError.image,
                    message: ex.message,
                    description: 'دقایقی دیگر منتظرتان هستیم.',
                    timeout: dayConfig
                        .gregory(ex.disconnectToDate)
                        .add(1, 'minute')
                        .toDate(),
                };
            }
            if (ex instanceof TrainNoServerResponseError) {
                this.listErrorReason = {
                    image: this.unknownListError.image,
                    message: ex.message,
                    type: 'Unknown',
                    id: 'server-no-response',
                    description: '',
                };
            }
        } finally {
            // Loading prices for the next month
            const currentSearchForm = useCurrentSearchForm();
            if (!params.inForm) {
                currentSearchForm.value.loadPrices();
            }
            // Load alternative suggestions when we just loaded the main reserve's results
            if (
                this.isTripModule() &&
                this.originLocation &&
                this.destinationLocation
            ) {
                searchFormStore.loadSuggestions({
                    origin: this.originLocation,
                    destination: this.destinationLocation,
                });
            }
        }
    }

    /**
     * load available laterals
     *
     * @memberof ItineraryModule
     */
    abstract loadLaterals(): Promise<void>;

    /**
     * GETTER - Indicates whether the specified set is loaded, or not
     *
     * @memberof ItineraryModule
     */
    isSetLoaded(setIndex: number) {
        const itineraryPackageSet = this.itineraryPackageSets[setIndex];
        return itineraryPackageSet?.isLoaded;
    }

    get hasItinerarySets(): boolean {
        return this.itineraryPackageSets.some(p => !!p);
    }

    /**
     * GETTER - Indicates whether all the packages in current page is full or not
     *
     * @memberof ItineraryModule
     */
    get areAllPackagesFull() {
        return !this.reservableListWithoutLaterals.length;
    }

    /**
     * GETTER - Returns the reservable sorted list of itinerary packages without laterals
     *
     * @memberof ItineraryModule
     */
    get reservableListWithoutLaterals() {
        return this.sortedReservableList.filter(
            p => p.reserveType === this.reserveInfo.type
        );
    }

    get supportedTrackingTypes(): TrackingType[] {
        return [];
    }

    get trackingTypes(): TrackingType[] {
        if (this.currentLoadingStatus !== LoadingStatus.Success) {
            return [];
        }
        return this.supportedTrackingTypes;
    }

    /**
     * parse path and do reserve process base on query strings
     *
     * @abstract
     * @param {string} path
     * @return {*}  {Promise<void>}
     * @memberof ItineraryModule
     */
    abstract formAutoReserve(path: LocationQuery): Promise<void>;

    /**
     * get list of filters
     *
     * @readonly
     * @abstract
     * @type {Filter[]}
     * @memberof ItineraryModule
     */
    abstract get filters(): Filter[];

    get filtersLoading() {
        return !this.currentAllPackagesLoaded;
    }

    sortFilters(filters: Filter[]) {
        const prevCounts = process.client
            ? (JSON.parse(localStorage.getItem('filters') ?? '{}') as Record<
                  string,
                  number
              >)
            : {};
        return filters
            .map(filter => ({
                ...filter,
                usage: prevCounts[filter.name] || 0,
            }))
            .sort((a, b) => b.usage - a.usage);
    }

    /**
     * GETTER - Returns the list of promoted itinerary packages, to be shown in a special section
     *
     * @memberof ItineraryModule
     */
    get promotedList() {
        if (!this.currentItineraryPackages) {
            return [];
        }
        return this.list.filter((pkg: ItineraryPackage) => pkg.isPromoted);
    }

    /**
     * GETTER - The count of itinerary packages, before filters being applied
     *
     * @memberof ItineraryModule
     */
    get unfilteredCount() {
        if (!this.currentItineraryPackages) {
            return 0;
        }
        return this.currentItineraryPackages.length;
    }

    /**
     * GETTER - Returns the list of available ItineraryPackage packages
     *
     * @memberof ItineraryModule
     */
    get list(): TPackage[] {
        if (!this.currentItineraryPackages || !this.currentPackageSet) {
            return [];
        }
        return this.currentItineraryPackages.filter(pkg =>
            this.applyFilter(pkg, this.currentPackageSet!.filters)
        );
    }

    get srpImageAlt(): string | null {
        return `رزرو ${this.reserveInfo.title}`;
    }

    /**
     * UTILITY - When overridden, applies the values of filter to a itinerary package, and return true if the package satisfies the filters
     *
     * @abstract
     * @param {ItineraryPackage} pkg
     * @param {{ [key: string]: any }} filters
     * @returns {boolean}
     * @memberof ItineraryModule
     */
    abstract applyFilter(pkg: TPackage, filters: Record<string, any>): boolean;

    /**
     * GETTER - Returns the itinerary packages from the current set
     */
    get currentItineraryPackages() {
        return this.currentPackageSet?.list || null;
    }

    /**
     * get current loading status
     *
     * @readonly
     * @memberof ItineraryModule
     */
    get currentLoadingStatus() {
        return this.currentPackageSet?.loadingStatus;
    }

    get isRealTimeLoading() {
        return (
            this.currentPackageSet?.loadingStatus ===
            LoadingStatus.RealtimeLoading
        );
    }

    /**
     * get current all package loaded or not
     *
     * @readonly
     * @memberof ItineraryModule
     */
    get currentAllPackagesLoaded() {
        return (
            this.currentPackageSet?.loadingStatus !==
            LoadingStatus.RealtimeLoading
        );
    }

    get currentSetIndex(): number {
        return useItineraryStore().currentSetIndex;
    }

    get currentPackageSet(): TPackageSet | null | undefined {
        return this.itineraryPackageSets[this.currentSetIndex];
    }

    /**
     * get the title of current itinerary
     *
     * @readonly
     * @abstract
     * @type {string}
     * @memberof ItineraryModule
     */
    abstract get mainTitle(): string;

    abstract get dateTitle(): string;

    static revivePackage(packageLike: ItineraryPackage) {
        let pack: ItineraryPackage | undefined;
        switch (packageLike.concreteType) {
            case ReserveType.Flight:
                pack = FlightPackage.revive(packageLike as FlightPackage);
                break;
            case ReserveType.Train:
                pack = TrainPackage.revive(packageLike as TrainPackage);
                break;
            case ReserveType.Hotel:
                pack = HotelPackage.revive(packageLike as HotelPackage);
                break;
            case ReserveType.Bus:
                pack = BusPackage.revive(packageLike as BusPackage);
                break;
            case ReserveType.Route:
                pack = RoutePackage.revive(packageLike as RoutePackage);
                break;
        }
        if (!pack) {
            return packageLike;
        }
        return pack;
    }

    isTripModule(): this is TripModule {
        return this.reserveInfo.type !== ReserveType.Hotel;
    }

    toJSON() {
        return { ...this };
    }
}
